import DOMPurify from 'dompurify';

// Configure DOMPurify with strict settings
const sanitizeConfig = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'a'],
  ALLOWED_ATTR: ['href', 'target'],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input', 'textarea', 'button', 'select', 'option'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onmouseenter', 'onmouseleave', 'onblur', 'onfocus'],
  SANITIZE_DOM: true,
  WHOLE_DOCUMENT: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM: false,
  RETURN_TRUSTED_TYPE: false
};

/**
 * Enhanced sanitizer service to protect against XSS and other injection attacks
 */
export const sanitizerService = {
  // Basic text sanitizer - removes HTML tags and special characters
  sanitizeText: (text: string): string => {
    if (!text) return '';
    
    // Convert to string if not already
    const str = String(text);
    
    // First encode HTML entities
    const encoded = str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
    
    return encoded;
  },
  
  // HTML sanitizer for rich text fields
  sanitizeHTML: (html: string): string => {
    if (!html) return '';
    
    // Convert to string if not already
    const str = String(html);
    
    // Remove potentially dangerous tags and attributes
    return str
      // Remove script tags and contents
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      // Remove event handlers
      .replace(/\s*(on\w+)="[^"]*"/gi, '')
      // Remove javascript: URLs
      .replace(/\s*javascript:[^"']*/gi, '')
      // Remove data: URLs
      .replace(/\s*data:[^"']*/gi, '')
      // Remove iframe tags
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      // Remove object tags
      .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
      // Remove embed tags
      .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '');
  },
  
  // URL sanitizer - only allow http and https URLs
  sanitizeURL: (url: string): string => {
    if (!url) return '';
    
    // Convert to string if not already
    const str = String(url);
    
    // Check if URL is valid and has an acceptable protocol
    try {
      const parsedURL = new URL(str);
      if (parsedURL.protocol !== 'http:' && parsedURL.protocol !== 'https:') {
        return '';
      }
      return str;
    } catch (error) {
      // If URL is malformed, return empty string
      return '';
    }
  },
  
  // SQL injection prevention for strings used in dynamic queries
  sanitizeForSQL: (input: string): string => {
    if (!input) return '';
    
    // Convert to string if not already
    const str = String(input);
    
    // Remove SQL injection patterns
    return str
      .replace(/'/g, "''")        // Escape single quotes
      .replace(/;/g, '')         // Remove semicolons
      .replace(/--/g, '')        // Remove SQL comments
      .replace(/\/\*/g, '')      // Remove block comment starts
      .replace(/\*\//g, '')      // Remove block comment ends
      .replace(/xp_/gi, '')      // Remove xp_ prefixes (stored procedures)
      .replace(/EXEC/gi, '')     // Remove EXEC
      .replace(/EXECUTE/gi, '')  // Remove EXECUTE
      .trim();                   // Remove leading/trailing whitespace
  },

  // Sanitize object with string values
  sanitizeObject<T extends Record<string, any>>(obj: T): T {
    const sanitized: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        sanitized[key] = this.sanitizeText(value);
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeObject(value);
      } else {
        sanitized[key] = value;
      }
    }
    
    return sanitized as T;
  }
}; 